
import Vue from 'vue';
import '@/plugins/chat';
import store from '@/store';
import { getModule } from 'vuex-module-decorators';
import ChatModule, { Chat } from '../../store/modules/Chat';
import { Prop, Component } from 'vue-property-decorator';
import SessionsModule from '../../store/modules/Sessions';

const chatModule = getModule(ChatModule, store);
const sessionsModule = getModule(SessionsModule, store);

@Component
export default class ChatWindow extends Vue {
    @Prop({
        required: true
    })
    public chat!: Chat;

    public participants = [];

    public colors = {
        header: {
            bg: '#161719',
            text: '#ecf0f1'
        },
        launcher: {
            bg: '#34495e'
        },
        messageList: {
            bg: '#161719'
        },
        sentMessage: {
            bg: '#00000000',
            text: '#ffffff'
        },
        receivedMessage: {
            bg: '#ff4081',
            text: '#ffffff'
        },
        userInput: {
            bg: '#161719',
            text: '#ffffff'
        },
        userList: {
            bg: '#161719',
            text: '#ecf0f1'
        }
    };

    public titleImageUrl = '/img/messages_icon.jpg';
    private pasteListener?: (e: Event) => void;
    private resizeObserver?: ResizeObserver;

    private dragListeners: {
        dragHandler?: (e: MouseEvent) => void;
        dragEndHandler?: () => void;
    } = {};

    private resizeDebounceTimer?: number;

    // Add these properties to track drag state
    private currentX = 0;
    private currentY = 0;
    private initialX = 0;
    private initialY = 0;
    private xOffset = 0;
    private yOffset = 0;

    closeChat() {
        chatModule.closeChat();
    }

    sendMessage(message: any) {
        chatModule.sendMessage({
            clientId: this.chat.clientId,
            sessionId: this.chat.sessionId,
            text: message.data.text
        });
    }

    mounted() {
        const chatElement = (this.$refs.chat as Vue).$el;
        const ce = chatElement.querySelector('[contenteditable]');
        const listener = (e: Event) => {
            e.preventDefault();
            const text = (e as unknown as { clipboardData: any }).clipboardData.getData('text/plain') as string;
            document.execCommand('insertText', false, text);
        };
        this.pasteListener = listener;
        ce!.addEventListener('paste', listener);
        
        // Add drag functionality to the container
        this.makeDraggable();
        
        // Add resize observer to monitor chat size changes
        this.setupResizeObserver();
    }

    unmounted() {
        if (this.pasteListener) {
            const chatElement = (this.$refs.chat as Vue).$el;
            const ce = chatElement.querySelector('[contenteditable]');
            ce!.removeEventListener('paste', this.pasteListener);
        }

        // Clean up resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        // Clean up debounce timer
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
        }

        // Clean up drag listeners
        this.cleanupDragListeners();
    }

    focus() {
        (this.$refs.chat as Vue).$root.$emit('focusUserInput');
    }

    private getNameForSessionId(sessionId: string) {
        return sessionsModule.sessionNames[sessionId];
    }

    private setupResizeObserver() {
        const container = this.$refs.chatContainer as HTMLElement;
        if (!container) return;

        // Create a ResizeObserver to watch for size changes
        this.resizeObserver = new ResizeObserver((entries) => {
            // Debounce the resize handling to avoid excessive calls
            clearTimeout(this.resizeDebounceTimer);
            this.resizeDebounceTimer = setTimeout(() => {
                this.adjustPositionIfNeeded();
            }, 100) as unknown as number;
        });

        // Start observing the container
        this.resizeObserver.observe(container);
    }

    private adjustPositionIfNeeded() {
        const container = this.$refs.chatContainer as HTMLElement;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const containerHeight = rect.height;
        const containerWidth = rect.width;

        // Check if chat extends beyond the top of the viewport
        if (rect.top < 0) {
            // If we're using right/bottom positioning (initial state)
            if (container.style.right !== 'auto') {
                // Switch to transform-based positioning
                container.style.right = 'auto';
                container.style.bottom = 'auto';
                container.style.left = '0';
                container.style.top = '0';
                
                // Position it at the bottom-right corner, respecting viewport bounds
                const maxX = window.innerWidth - containerWidth;
                const maxY = window.innerHeight - containerHeight;
                const targetX = Math.max(0, Math.min(maxX, window.innerWidth - containerWidth - 20));
                const targetY = Math.max(0, Math.min(maxY, window.innerHeight - containerHeight - 20));
                
                container.style.transform = `translate(${targetX}px, ${targetY}px)`;
                
                // Update internal drag state
                this.currentX = targetX;
                this.currentY = targetY;
                this.xOffset = targetX;
                this.yOffset = targetY;
            } else {
                // Already using transform positioning, just adjust Y
                const currentTransform = container.style.transform;
                const match = currentTransform.match(/translate\((-?\d+(?:\.\d+)?)px,\s*(-?\d+(?:\.\d+)?)px\)/);
                
                if (match) {
                    const currentX = parseFloat(match[1]);
                    let currentY = parseFloat(match[2]);
                    
                    // Keep within viewport bounds
                    const maxX = window.innerWidth - containerWidth;
                    const maxY = window.innerHeight - containerHeight;
                    const newX = Math.max(0, Math.min(currentX, maxX));
                    const newY = Math.max(0, Math.min(currentY, maxY));
                    
                    container.style.transform = `translate(${newX}px, ${newY}px)`;
                    
                    // Update internal drag state
                    this.currentX = newX;
                    this.currentY = newY;
                    this.xOffset = newX;
                    this.yOffset = newY;
                }
            }
        }
    }

    private makeDraggable() {
        const container = this.$refs.chatContainer as HTMLElement;
        const dragHandle = this.$refs.dragHandle as HTMLElement;
        
        if (!container || !dragHandle) return;

        let isDragging = false;

        // Position the container initially
        container.style.position = 'fixed';
        container.style.right = '20px';
        container.style.bottom = '20px';
        container.style.zIndex = '1000';

        const dragStart = (e: MouseEvent) => {
            // Don't drag if clicking on the close button
            const target = e.target as HTMLElement;
            if (target.classList.contains('drag-close-btn')) {
                return;
            }

            // Get current position of the container
            const rect = container.getBoundingClientRect();
            
            // If this is the first drag, calculate the current position
            if (this.xOffset === 0 && this.yOffset === 0) {
                // Container is positioned with right/bottom, so calculate the equivalent left/top
                this.xOffset = rect.left;
                this.yOffset = rect.top;
                this.currentX = this.xOffset;
                this.currentY = this.yOffset;
            }

            this.initialX = e.clientX - this.xOffset;
            this.initialY = e.clientY - this.yOffset;
            isDragging = true;
            dragHandle.style.cursor = 'grabbing';
            
            // Prevent text selection
            e.preventDefault();
        };

        const dragEnd = () => {
            this.initialX = this.currentX;
            this.initialY = this.currentY;
            isDragging = false;
            dragHandle.style.cursor = 'move';
        };

        const drag = (e: MouseEvent) => {
            if (isDragging) {
                e.preventDefault();
                
                this.currentX = e.clientX - this.initialX;
                this.currentY = e.clientY - this.initialY;
                this.xOffset = this.currentX;
                this.yOffset = this.currentY;

                // Keep chat window within viewport bounds
                const rect = container.getBoundingClientRect();
                const containerWidth = rect.width;
                const containerHeight = rect.height;
                const maxX = window.innerWidth - containerWidth;
                const maxY = window.innerHeight - containerHeight;
                
                this.currentX = Math.max(0, Math.min(this.currentX, maxX));
                this.currentY = Math.max(0, Math.min(this.currentY, maxY));
                
                // Switch to left/top positioning on first drag
                if (container.style.right !== 'auto') {
                    container.style.right = 'auto';
                    container.style.bottom = 'auto';
                    container.style.left = '0';
                    container.style.top = '0';
                }
                
                container.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
            }
        };

        // Store listeners for cleanup
        this.dragListeners.dragHandler = drag;
        this.dragListeners.dragEndHandler = dragEnd;

        dragHandle.addEventListener('mousedown', dragStart);
        document.addEventListener('mousemove', drag);
        document.addEventListener('mouseup', dragEnd);
    }

    private cleanupDragListeners() {
        if (this.dragListeners.dragHandler) {
            document.removeEventListener('mousemove', this.dragListeners.dragHandler);
        }
        if (this.dragListeners.dragEndHandler) {
            document.removeEventListener('mouseup', this.dragListeners.dragEndHandler);
        }
    }
};
